import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { pinFor, addTiles } from './mapPins'

/**
 * The map on the order screen: where the parcel is going, and where the rider is now.
 *
 * **Read-only, and a sibling of `LeafletMap` rather than a mode of it.** That component
 * exists to answer a question — one draggable pin, a tap that moves it, a callback that
 * reports where it landed, and machinery to stop the ground shifting under somebody's
 * thumb mid-drag. This one asks nothing. It shows two or three fixed points and lets a
 * person waiting for their delivery look at them. Folding the two together would put a
 * second set of branches through the order form's picker, which is on the money path.
 *
 * What they genuinely share — the pin art and the tile layer — lives in `mapPins.js` and
 * is imported by both.
 *
 * **No route line, and no routing API.** See docs/intent/public-order-tracking.md: road
 * routing is the one part of this feature with a monthly bill, and it answers a question
 * ("which street?") that nobody asked. The question asked was "where are they?".
 */

/**
 * @param {Array<{key: string, lat: number, lng: number, variant: string, label?: string}>} pins
 */
export default function TrackingMap({ pins = [], className = '' }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef(new Map())

  /**
   * Whether the map has already framed itself.
   *
   * Fitting the bounds is right **once**, on arrival, so the first thing somebody sees is
   * both ends of the journey. Doing it again on every poll is wrong: the rider pin moves
   * every thirty seconds, and a map that re-frames itself twice a minute would yank the
   * view back from wherever the person had just zoomed in to look. After the first frame,
   * pins move and the viewport stays where it was put.
   */
  const framedRef = useRef(false)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      zoomControl: true,
      // Pannable and zoomable, unlike the form's still preview: the whole point is to
      // look closer at where the rider has got to.
      dragging: true,
      scrollWheelZoom: false,
      touchZoom: true,
      // The page scrolls under this on a phone. A wheel that zooms the map instead of
      // scrolling the page traps the reader; pinch and the buttons still zoom.
      doubleClickZoom: true,
      keyboard: true,
    })

    addTiles(map)
    mapRef.current = map

    // The container is often still being laid out when the map is created, so the first
    // paint can happen at zero height and render no tiles at all.
    const settle = setTimeout(() => map.invalidateSize(), 60)
    const onResize = () => map.invalidateSize()
    window.addEventListener('resize', onResize)

    return () => {
      clearTimeout(settle)
      window.removeEventListener('resize', onResize)
      map.remove()
      mapRef.current = null
      markersRef.current = new Map()
      framedRef.current = false
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // A pin with no coordinates is a pin we do not have yet — a rider who has never
    // reported, most often. It is left off the map rather than dropped at [0, 0], which
    // is in the Atlantic.
    const placed = pins.filter((pin) => (
      Number.isFinite(Number(pin?.lat)) && Number.isFinite(Number(pin?.lng))
    ))

    const seen = new Set()

    placed.forEach((pin) => {
      const at = [Number(pin.lat), Number(pin.lng)]
      const existing = markersRef.current.get(pin.key)

      seen.add(pin.key)

      if (existing) {
        // Moved, not recreated. Recreating the rider marker every thirty seconds would
        // make it blink, and would throw away any popup the reader had open.
        existing.setLatLng(at)
        return
      }

      const marker = L.marker(at, { icon: pinFor(pin.variant), keyboard: false })

      if (pin.label) {
        marker.bindTooltip(pin.label, { direction: 'top', offset: [0, -8] })
      }

      marker.addTo(map)
      markersRef.current.set(pin.key, marker)
    })

    // A pin that has gone away — the rider position on an order that just closed.
    markersRef.current.forEach((marker, key) => {
      if (seen.has(key)) return

      map.removeLayer(marker)
      markersRef.current.delete(key)
    })

    if (!placed.length) return

    if (!framedRef.current) {
      framedRef.current = true

      const bounds = L.latLngBounds(placed.map((pin) => [Number(pin.lat), Number(pin.lng)]))

      // `maxZoom` matters when the two ends are metres apart — a tight fit would zoom to
      // street level and show a person nothing but their own roof.
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 16 })
    }
  }, [pins])

  return (
    <div
      ref={containerRef}
      className={`bg-surface-2 ${className}`}
      // Leaflet renders no tiles at all without a real height.
      style={{ minHeight: '220px' }}
      // It is decoration for a screen-reader: every coordinate on it is already written
      // out as text in the journey rail directly above.
      aria-hidden="true"
    />
  )
}
